import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Brain, Sparkles, Send, ArrowRight, CheckCircle2, ShieldAlert, Bot,
  Users, Briefcase, Landmark, GraduationCap, Activity, TrendingUp, ClipboardCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const METRIC_DEFS = [
  { key: "new_family_leads", label: "New family inquiries", icon: Users, tone: "info", link: "/ops/crm" },
  { key: "business_leads", label: "Active business leads", icon: Briefcase, tone: "info", link: "/ops/crm" },
  { key: "ready_for_review", label: "Ready for human review", icon: ClipboardCheck, tone: "warn", link: "/ops/recruiting" },
  { key: "new_applicants", label: "New applicants", icon: Users, tone: "info", link: "/ops/recruiting" },
  { key: "caregivers_available", label: "Caregivers available", icon: Users, tone: "good", link: "/marketplace" },
  { key: "credential_gaps", label: "Credential gaps", icon: ShieldAlert, tone: "warn", link: "/workforce" },
  { key: "pending_approvals", label: "Awaiting your approval", icon: ShieldAlert, tone: "warn", link: "/ops/approvals" },
  { key: "gov_opportunities", label: "Government opportunities", icon: Landmark, tone: "info", link: "/opportunities" },
];

const AGENT_ICON = { concierge: Bot, sales: TrendingUp, recruiting: Users, scheduling: Activity, training: GraduationCap, support: Bot, billing: TrendingUp, compliance: ShieldAlert, enterprise: Briefcase, government: Landmark, executive: Brain };
const TONE = { info: "text-sky-300", warn: "text-amber-300", good: "text-teal-300" };

export default function OpsCenter() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [thinking, setThinking] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    api.get("/ops/briefing").then((r) => setBrief(r.data)).catch(() => toast.error("Could not load briefing"));
    api.get("/ops/agents").then((r) => setAgents(r.data.agents)).catch(() => {});
  }, [user, loading, navigate]);

  const ask = async (question) => {
    const text = question || q;
    if (!text.trim()) return;
    setQ(text); setThinking(true); setAnswer("");
    try { const { data } = await api.post("/brain/ask", { question: text }); setAnswer(data.reply); }
    catch { toast.error("T5 Brain is unavailable right now"); }
    finally { setThinking(false); }
  };

  if (loading || !user || !brief) return <div className="py-32 grid place-items-center bg-blue-950 min-h-[70vh]"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;

  const m = brief.metrics;
  const money = (n) => "$" + Number(n || 0).toLocaleString();

  return (
    <div className="bg-blue-950 text-white min-h-screen" data-testid="ops-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 mb-1"><Brain className="w-5 h-5 text-teal-400" /><p className="text-xs uppercase tracking-[0.18em] text-teal-400 font-semibold">T5 Brain · Autonomous Operations Center</p></div>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-3xl lg:text-5xl font-bold" data-testid="ops-greeting">{brief.greeting}</motion.h1>
        <p className="text-blue-200/70 mt-2">{brief.date} · <span className="text-teal-300">AI handles the repetitive work. You handle the important decisions.</span></p>

        {/* metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="ops-metrics">
          {METRIC_DEFS.map((d) => {
            const Icon = d.icon;
            return (
              <button key={d.key} onClick={() => navigate(d.link)} data-testid={`ops-metric-${d.key}`}
                className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-4 transition-colors">
                <Icon className={`w-5 h-5 ${TONE[d.tone]}`} />
                <p className="font-display text-3xl font-bold mt-2">{m[d.key] ?? 0}</p>
                <p className="text-xs text-blue-200/70 mt-0.5">{d.label}</p>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <div className="bg-teal-500/15 border border-teal-400/30 rounded-2xl p-4"><TrendingUp className="w-5 h-5 text-teal-300" /><p className="font-display text-2xl font-bold mt-2" data-testid="ops-pipeline">{money(m.pipeline_value)}</p><p className="text-xs text-teal-100/80">Open pipeline value</p></div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4"><GraduationCap className="w-5 h-5 text-sky-300" /><p className="font-display text-2xl font-bold mt-2">{m.training_completion == null ? "—" : m.training_completion + "%"}</p><p className="text-xs text-blue-200/70">Training completion</p></div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4"><ShieldAlert className="w-5 h-5 text-amber-300" /><p className="font-display text-2xl font-bold mt-2">{m.reg_changes}</p><p className="text-xs text-blue-200/70">Regulatory changes to review</p></div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4"><Users className="w-5 h-5 text-sky-300" /><p className="font-display text-2xl font-bold mt-2">{m.training_gaps}</p><p className="text-xs text-blue-200/70">Training gaps</p></div>
        </div>

        {/* recommended priorities */}
        <div className="mt-8 bg-gradient-to-r from-teal-500/15 to-transparent border border-teal-400/20 rounded-2xl p-5" data-testid="ops-priorities">
          <p className="text-xs uppercase tracking-wide text-teal-300 font-semibold mb-3">Recommended priorities today</p>
          <div className="space-y-2">
            {brief.priorities.map((p, i) => (
              <Link key={i} to={p.link} className="flex items-center gap-3 group" data-testid={`ops-priority-${i}`}>
                <span className="grid place-items-center w-6 h-6 rounded-full bg-teal-500 text-white text-xs font-bold shrink-0">{i + 1}</span>
                <span className="text-blue-50 group-hover:text-teal-300 transition-colors">{p.text}</span>
                <ArrowRight className="w-4 h-4 text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>

        {/* two columns: AI handled + Approvals */}
        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="ops-ai-handled">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold flex items-center gap-2"><Bot className="w-5 h-5 text-teal-400" /> AI handled</p>
              <Link to="/ops/activity" className="text-xs text-teal-300 hover:underline">Activity log</Link>
            </div>
            <div className="space-y-2.5">
              {brief.ai_handled.length === 0 && <p className="text-sm text-blue-200/60">No automated actions yet.</p>}
              {brief.ai_handled.slice(0, 8).map((a) => (
                <div key={a.id} className="flex gap-3 text-sm" data-testid="ops-activity-item">
                  <CheckCircle2 className="w-4 h-4 text-teal-400 shrink-0 mt-0.5" />
                  <div><p className="text-blue-50">{a.action}</p>{a.detail && <p className="text-xs text-blue-200/60">{a.detail}</p>}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-amber-400/20 rounded-2xl p-5" data-testid="ops-approvals">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-amber-300" /> Owner approval required</p>
              <Link to="/ops/approvals" className="text-xs text-amber-300 hover:underline">Open Approval Center</Link>
            </div>
            <div className="space-y-2.5">
              {brief.approvals.length === 0 && <p className="text-sm text-blue-200/60">Nothing needs your approval right now.</p>}
              {brief.approvals.slice(0, 6).map((a) => (
                <Link key={a.id} to="/ops/approvals" className="block bg-white/5 hover:bg-white/10 rounded-xl p-3 transition-colors" data-testid="ops-approval-item">
                  <div className="flex items-center justify-between"><span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200">{a.category}</span><span className="text-[10px] text-blue-200/60">{a.risk} risk</span></div>
                  <p className="text-sm text-blue-50 mt-1">{a.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* T5 Brain ask (management only) */}
        {["admin", "executive", "manager"].includes(user.role) && <div className="mt-8 bg-white/5 border border-white/10 rounded-2xl p-5" data-testid="ops-brain">
          <p className="font-semibold flex items-center gap-2 mb-3"><Brain className="w-5 h-5 text-teal-400" /> Ask T5 Executive Intelligence</p>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Sparkles className="w-4 h-4 text-teal-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} data-testid="brain-input"
                placeholder="What changed overnight? Which shifts are at risk? Prepare my ops meeting…" className="pl-9 bg-white/10 border-white/15 text-white placeholder:text-blue-200/50 h-12" />
            </div>
            <Button onClick={() => ask()} data-testid="brain-ask" className="h-12 px-6 bg-teal-500 hover:bg-teal-600 gap-2"><Send className="w-4 h-4" /> Ask</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {["What changed overnight?", "Which leads need attention?", "Prepare my weekly operations meeting", "How many caregivers are available?"].map((s) => (
              <button key={s} onClick={() => ask(s)} className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-blue-100 transition-colors">{s}</button>
            ))}
          </div>
          {thinking && <div className="mt-4 flex items-center gap-2 text-blue-100" data-testid="brain-thinking"><Loader2 className="w-4 h-4 animate-spin text-teal-400" /> Analyzing your live operation…</div>}
          {answer && !thinking && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 bg-blue-900/40 border border-teal-400/20 rounded-xl p-4 text-blue-50 whitespace-pre-wrap leading-relaxed" data-testid="brain-answer">{answer}</motion.div>}
        </div>}

        {/* agents */}
        <div className="mt-8">
          <p className="text-xs uppercase tracking-wide text-teal-300 font-semibold mb-3">T5 Brain — specialized agents</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ops-agents">
            {agents.map((a) => {
              const Icon = AGENT_ICON[a.key] || Bot;
              return (
                <div key={a.key} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`ops-agent-${a.key}`}>
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-9 h-9 rounded-xl bg-teal-500/20 text-teal-300"><Icon className="w-5 h-5" /></span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.kind === "ai" ? "bg-teal-500/20 text-teal-200" : "bg-white/10 text-blue-200"}`}>{a.kind === "ai" ? "Active" : "Soon"}</span>
                  </div>
                  <p className="font-semibold mt-2 text-sm">{a.name}</p>
                  <p className="text-xs text-blue-200/60 mt-0.5">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild className="bg-teal-500 hover:bg-teal-600"><Link to="/ops/crm" data-testid="ops-goto-crm">Open CRM</Link></Button>
          <Button asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Link to="/ops/recruiting" data-testid="ops-goto-recruiting">Recruiting Pipeline</Link></Button>
          <Button asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Link to="/ops/scheduling" data-testid="ops-goto-scheduling">Scheduling</Link></Button>
          <Button asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Link to="/ops/approvals" data-testid="ops-goto-approvals">Approval Center</Link></Button>
          {user.role === "admin" && <Button asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Link to="/ops/team" data-testid="ops-goto-team">Team &amp; Roles</Link></Button>}
          <Button asChild variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10"><Link to="/ops/enterprise" data-testid="ops-goto-enterprise">Enterprise &amp; Government</Link></Button>
        </div>
      </div>
    </div>
  );
}
